/**
 * useEditConnector — PUT /enterprise/connectors/{conn_id}.
 *
 * Mirror of V1 ``enterprise.ts::updateConnector`` (line 181). Update
 * name + credentials + config in a single call; the BE merges credentials
 * server-side (blank fields preserve the existing encrypted secret —
 * callers strip empty password inputs before calling).
 *
 * ``onSuccess`` invalidates BOTH the per-capability list (so the row's
 * label updates) AND the per-connection schema/profiling caches (the
 * underlying connection may now point at a different DB host — schema
 * could be stale).
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  type EnterpriseConnectorDetail,
  type UpdateConnectorRequest,
  updateEnterpriseConnector,
} from "@ando/core/api";

import { connectorsListKey } from "../catalog";

import { enterpriseProfilingKey, enterpriseSchemaKey } from "./enterpriseKeys";

export interface UseEditConnectorVars {
  readonly connId: string;
  readonly capability: string;
  readonly body: UpdateConnectorRequest;
}

export function useEditConnector() {
  const qc = useQueryClient();
  return useMutation<EnterpriseConnectorDetail, Error, UseEditConnectorVars>({
    mutationFn: ({ connId, body }) => updateEnterpriseConnector(connId, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: connectorsListKey(vars.capability),
      });
      void qc.invalidateQueries({
        queryKey: enterpriseSchemaKey(vars.connId),
      });
      void qc.invalidateQueries({
        queryKey: enterpriseProfilingKey(vars.connId),
      });
    },
  });
}
